import bpmnFontCssUrl from 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css?url'
import bpmnJsCssUrl from 'bpmn-js/dist/assets/bpmn-js.css?url'
import bpmnDiagramJsCssUrl from 'bpmn-js/dist/assets/diagram-js.css?url'
import BpmnModeler from 'bpmn-js/lib/Modeler'
import NavigatedViewer from 'bpmn-js/lib/NavigatedViewer'
import BpmnColorPickerModule from 'bpmn-js-color-picker'
import bpmnColorPickerCssUrl from 'bpmn-js-color-picker/colors/color-picker.css?url'
import NativeCopyPasteModule from 'bpmn-js-native-copy-paste'
import { createReviver } from 'bpmn-js-native-copy-paste/lib/PasteUtil.js'
import type { ReactNode, RefObject } from 'react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { exportModelToPptx, exportModelToVisio } from '@/client/sdk.gen'
import { Spinner } from '@/components/ui/spinner'
import { useProductTourOptional } from '@/context/ProductTourContext'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR } from '@/lib/product-tour/constants'
import { captureUnexpectedError } from '@/utils/error'
// Import bpmn-js CSS for palette and UI elements
import 'bpmn-js/dist/assets/diagram-js.css'
import 'bpmn-js/dist/assets/bpmn-js.css'
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css'
import 'bpmn-js-color-picker/colors/color-picker.css'
import './diff/bpmn-diff-overlay.css'
import { BpmnDiagramControls } from './BpmnDiagramControls'
import { BpmnExportMenu } from './BpmnExportMenu'
import { BpmnModelSaveControls } from './BpmnModelSaveControls'
import type { BpmnExternalToolbarApi } from './bpmnExternalToolbar'
import { useBpmnAutosave } from './hooks/useBpmnAutosave'
import { ImprovementCanvasLayer } from './improvements/ImprovementCanvasLayer'
import './improvements/improvement-overlays.css'
import './improvements/spotlight.css'
import {
	createModelEditCapture,
	type ModelEditCaptureController,
	type ModelEditEvent,
} from './hooks/useModelEditCapture'
import {
	normalizeBpmnXmlForCompare,
	shouldSkipStaleBpmnReimport,
} from './utils/bpmnXmlImportGuards'

export type BpmnCanvasInstance = BpmnModeler | InstanceType<typeof NavigatedViewer>

/** Prefix used by bpmn-js-native-copy-paste to identify BPMN clipboard data. */
const NATIVE_CLIPBOARD_PREFIX = 'bpmn-js-clip----'

function injectStylesheetOnce(id: string, href: string): Promise<void> {
	return new Promise((resolve) => {
		if (document.getElementById(id)) {
			resolve()
			return
		}
		const link = document.createElement('link')
		link.id = id
		link.rel = 'stylesheet'
		link.href = href
		link.onload = () => resolve()
		link.onerror = () => resolve()
		document.head.appendChild(link)
	})
}

interface Canvas {
	zoom(): number
	zoom(value: number | 'fit-viewport'): number
	isFocused(): boolean
}

interface CopyPasteLike {
	paste(context: { tree: unknown }): void
}

interface ModdleLike {
	getTypeDescriptor(type: string): unknown
	create(type: string, attrs: Record<string, unknown>): unknown
}

interface CommandStackLike {
	canUndo(): boolean
	canRedo(): boolean
	undo(): void
	redo(): void
}

interface EventBusLike {
	on(event: string, callback: (payload: { message?: string; error?: unknown }) => void): void
}

interface NativeCopyPasteLike {
	toggle(enabled: boolean): void
}

interface SearchPadLike {
	open(): void
}

interface BpmnEditorProps {
	initialXml: string | null
	/** Resets autosave debounce/queue when this changes (e.g. model snapshot version) */
	autosaveContextKey?: string
	onSave?: (xml: string, events?: ModelEditEvent[]) => Promise<void>
	/**
	 * Capture typed BPMN edit events from the command stack and flush them with each
	 * save (model-deliverable attribution + semantic diff). Off by default so summary
	 * diagrams and the legacy snapshot path are unaffected.
	 */
	captureModelEvents?: boolean
	className?: string
	/** Rendered at the start of the toolbar (e.g. version navigation) */
	toolbarLeft?: ReactNode
	/** Rendered in the toolbar next to the Save button (e.g. optional badge/actions) */
	toolbarExtra?: ReactNode
	/** Called when unsaved state changes; used to block navigation */
	onUnsavedChange?: (hasUnsaved: boolean) => void
	/** Project ID for analysis model export to Visio (enables Export as Visio) */
	projectId?: string
	/** Analysis ID for analysis model export to Visio (enables Export as Visio) */
	analysisId?: string
	/** Registers a function to force-save current XML before leaving */
	registerManualSave?: (saveFn: (() => Promise<boolean>) | null) => void
	/** `external` hoists toolbar actions to a parent (unified summary model section). */
	toolbarPlacement?: 'inline' | 'external'
	/** Receives live toolbar state when `toolbarPlacement` is `external`. */
	onExternalToolbarApi?: (api: BpmnExternalToolbarApi | null) => void
	/** When toolbar is external, fullscreen this element (e.g. top bar + canvas) instead of the editor only. */
	fullscreenTargetRef?: RefObject<HTMLElement | null>
	/** Notified when the underlying bpmn-js instance is created or destroyed. */
	onModelerReady?: (modeler: BpmnCanvasInstance | null) => void
	/** View-only mode: NavigatedViewer (pan/zoom only, no edit). */
	readOnly?: boolean
	/** Optional banner above the toolbar (e.g. preview notice). */
	previewBanner?: ReactNode
}

export type { BpmnExternalToolbarApi } from './bpmnExternalToolbar'

const BpmnEditorInner = ({
	initialXml,
	autosaveContextKey,
	onSave,
	captureModelEvents = false,
	className = '',
	toolbarLeft,
	toolbarExtra,
	onUnsavedChange,
	projectId,
	analysisId,
	registerManualSave,
	toolbarPlacement = 'inline',
	onExternalToolbarApi,
	fullscreenTargetRef,
	onModelerReady,
	readOnly = false,
	previewBanner,
}: BpmnEditorProps) => {
	const { t } = useTranslation()
	const productTour = useProductTourOptional()
	const showTourTargets = Boolean(productTour?.isTourActive)
	const containerRef = useRef<HTMLDivElement>(null)
	const editorWrapperRef = useRef<HTMLDivElement>(null)
	const modelerRef = useRef<BpmnCanvasInstance | null>(null)
	const captureRef = useRef<ModelEditCaptureController | null>(null)
	const onSaveRef = useRef<typeof onSave>(onSave)
	const performManualSaveRef = useRef<(xml: string) => Promise<void>>(async () => {})
	const lastImportedNormalizedRef = useRef<string | null>(null)
	const lastSuccessfulImportContextRef = useRef<string | undefined>(undefined)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [isFullscreen, setIsFullscreen] = useState(false)
	const [modelerReady, setModelerReady] = useState(false)
	const [canUndo, setCanUndo] = useState(false)
	const [canRedo, setCanRedo] = useState(false)
	const [exportingFormat, setExportingFormat] = useState<
		'png' | 'bpmn' | 'svg' | 'visio' | 'pptx' | null
	>(null)
	const [xmlLoaded, setXmlLoaded] = useState(false)
	const [canvasBoundary, setCanvasBoundary] = useState<HTMLDivElement | null>(null)
	const onModelerReadyRef = useRef(onModelerReady)
	onModelerReadyRef.current = onModelerReady
	const readOnlyRef = useRef(readOnly)
	readOnlyRef.current = readOnly

	const activeModeler = modelerReady ? modelerRef.current : null

	useEffect(() => {
		onModelerReadyRef.current?.(activeModeler)
	}, [activeModeler])

	// Drain captured edit events at actual save time so they travel with the XML.
	// Leaves the autosave debounce/queue machinery XML-only.
	const handleSaveWithCapture = useCallback(
		async (xml: string) => {
			if (!onSave) return
			const events = captureModelEvents ? captureRef.current?.drain() : undefined
			await onSave(xml, events && events.length > 0 ? events : undefined)
		},
		[onSave, captureModelEvents]
	)

	const {
		saveStatus,
		isSaveInFlight,
		isSaveStalled,
		triggerSave,
		performManualSave,
		unsavedChangesRef,
	} = useBpmnAutosave({
		initialXml,
		autosaveContextKey,
		onSave: readOnly ? undefined : onSave ? handleSaveWithCapture : undefined,
	})

	performManualSaveRef.current = performManualSave

	useEffect(() => {
		if (!captureModelEvents || !activeModeler) {
			captureRef.current = null
			return
		}
		const controller = createModelEditCapture(
			activeModeler as unknown as { get: (s: string) => unknown }
		)
		captureRef.current = controller
		return () => {
			controller.detach()
			captureRef.current = null
		}
	}, [captureModelEvents, activeModeler])

	useEffect(() => {
		onSaveRef.current = onSave
	}, [onSave])

	// Warn on refresh/close while unsaved so debounced autosave can finish (SCRUM-304).
	useEffect(() => {
		if (!onSave) return
		const dirty = saveStatus === 'dirty' || saveStatus === 'saving' || saveStatus === 'error'
		if (!dirty) return
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault()
			e.returnValue = ''
		}
		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => window.removeEventListener('beforeunload', handleBeforeUnload)
	}, [onSave, saveStatus])

	// Notify parent when unsaved state changes (saving = changes pending; error = save failed)
	useEffect(() => {
		onUnsavedChange?.(saveStatus === 'dirty' || saveStatus === 'saving' || saveStatus === 'error')
	}, [saveStatus, onUnsavedChange])

	useEffect(() => {
		let cancelled = false

		const initializeModeler = async () => {
			try {
				setLoading(true)
				setError(null)

				// Ensure container has dimensions before initializing
				// Wait for next frame so the ref is attached (and handle Strict Mode unmount)
				await new Promise((resolve) => requestAnimationFrame(resolve))
				await new Promise((resolve) => setTimeout(resolve, 50))

				if (cancelled) return

				// Container may be null after delay if effect was cleaned up (e.g. Strict Mode or navigation)
				if (!containerRef.current) {
					setLoading(false)
					return
				}

				const container = containerRef.current
				if (!container) {
					setLoading(false)
					return
				}

				// Ensure container has explicit dimensions
				if (container.offsetHeight === 0 || container.offsetWidth === 0) {
					await new Promise((resolve) => setTimeout(resolve, 100))
				}

				if (cancelled) return
				if (!containerRef.current) {
					setLoading(false)
					return
				}

				const isViewerMode = readOnlyRef.current

				const stylesheetTasks = [
					injectStylesheetOnce('modelmatic-bpmn-diagram-js', bpmnDiagramJsCssUrl),
					injectStylesheetOnce('modelmatic-bpmn-js', bpmnJsCssUrl),
					injectStylesheetOnce('modelmatic-bpmn-font', bpmnFontCssUrl),
				]
				if (!isViewerMode) {
					stylesheetTasks.push(
						injectStylesheetOnce('modelmatic-bpmn-color-picker', bpmnColorPickerCssUrl)
					)
				}
				await Promise.all(stylesheetTasks)

				if (cancelled) return
				if (!containerRef.current) {
					setLoading(false)
					return
				}

				if (isViewerMode) {
					modelerRef.current = new NavigatedViewer({ container: containerRef.current })
				} else {
					modelerRef.current = new BpmnModeler({
						container: containerRef.current,
						additionalModules: [BpmnColorPickerModule, NativeCopyPasteModule],
					})

					const eventBus = modelerRef.current.get('eventBus') as EventBusLike
					const nativeCopyPaste = modelerRef.current.get('nativeCopyPaste') as NativeCopyPasteLike
					eventBus.on('native-copy-paste:error', ({ message, error }) => {
						const isPermissionDenied = error instanceof DOMException && error.name === 'NotAllowedError'

						if (isPermissionDenied) {
							console.warn(
								'[native-copy-paste] Clipboard read permission denied. Cross-tab paste requires clipboard access.',
								{ message, error }
							)
							toast.warning(t('components.bpmnEditor.clipboardPermissionDenied'), {
								duration: 6000,
							})
						} else {
							nativeCopyPaste.toggle(false)
							console.warn('Native BPMN copy/paste unavailable. Falling back to local clipboard.', {
								message,
								error,
							})
						}
					})

					const syncUndoRedoState = () => {
						const m = modelerRef.current
						if (!m) return
						const stack = m.get('commandStack') as CommandStackLike
						setCanUndo(stack.canUndo())
						setCanRedo(stack.canRedo())
					}

					modelerRef.current.on('commandStack.changed', () => {
						syncUndoRedoState()
						if (!onSaveRef.current) return
						modelerRef.current
							?.saveXML({ format: true })
							.then(({ xml }) => {
								if (!xml) return
								lastImportedNormalizedRef.current = normalizeBpmnXmlForCompare(xml)
								triggerSave(xml)
							})
							.catch((err) => console.warn('Failed to extract XML for autosave:', err))
					})
				}
				setModelerReady(true)

				// Fit to viewport after a small delay
				setTimeout(() => {
					if (modelerRef.current && !cancelled) {
						try {
							const canvas = modelerRef.current.get('canvas') as Canvas
							canvas.zoom('fit-viewport')
						} catch (zoomErr) {
							console.warn('Could not fit to viewport:', zoomErr)
						}
					}
				}, 100)

				if (!cancelled) setLoading(false)
			} catch (err) {
				if (cancelled) return
				console.error('Error initializing BPMN editor:', err)
				setError(err instanceof Error ? err.message : t('components.bpmnEditor.errorInitializing'))
				setLoading(false)
			}
		}

		initializeModeler()

		const attachPasteListener = !readOnlyRef.current

		const handleDocumentPaste = (e: ClipboardEvent) => {
			const m = modelerRef.current
			if (!m) return

			const canvas = m.get('canvas') as Canvas
			if (canvas.isFocused()) return

			const active = document.activeElement
			if (
				active instanceof HTMLInputElement ||
				active instanceof HTMLTextAreaElement ||
				(active instanceof HTMLElement && active.isContentEditable)
			) {
				return
			}

			const text = e.clipboardData?.getData('text/plain') ?? ''
			if (!text.startsWith(NATIVE_CLIPBOARD_PREFIX)) return

			e.preventDefault()

			try {
				const moddle = m.get('moddle') as ModdleLike
				const tree = JSON.parse(text.slice(NATIVE_CLIPBOARD_PREFIX.length), createReviver(moddle))
				const copyPasteService = m.get('copyPaste') as CopyPasteLike
				copyPasteService.paste({ tree })
			} catch (err) {
				console.warn('[BpmnEditor] Failed to parse BPMN clipboard data:', err)
			}
		}

		if (attachPasteListener) {
			document.addEventListener('paste', handleDocumentPaste)
		}

		return () => {
			cancelled = true
			lastImportedNormalizedRef.current = null
			lastSuccessfulImportContextRef.current = undefined
			if (attachPasteListener) {
				document.removeEventListener('paste', handleDocumentPaste)
			}
			if (modelerRef.current) {
				try {
					modelerRef.current.destroy()
				} catch (destroyErr) {
					console.warn('Error destroying modeler:', destroyErr)
				}
				modelerRef.current = null
			}
			setModelerReady(false)
			onModelerReadyRef.current?.(null)
		}
	}, [t, triggerSave, readOnly])

	useEffect(() => {
		if (!initialXml) {
			setLoading(false)
			return
		}

		if (!modelerReady) return

		const modeler = modelerRef.current
		if (!modeler) return

		const normalizedIncoming = normalizeBpmnXmlForCompare(initialXml)
		if (lastImportedNormalizedRef.current === normalizedIncoming) {
			setXmlLoaded(true)
			return
		}

		const layoutCtx = autosaveContextKey ?? ''
		const silentSameContextRefresh =
			layoutCtx !== '' && layoutCtx === lastSuccessfulImportContextRef.current

		let cancelled = false
		if (!silentSameContextRefresh) {
			setLoading(true)
		}
		setError(null)
		setXmlLoaded(false)

		const runImport = () =>
			modeler
				.importXML(initialXml)
				.then(() => {
					if (cancelled) return
					lastImportedNormalizedRef.current = normalizedIncoming
					lastSuccessfulImportContextRef.current = layoutCtx
					const canvas = modeler.get('canvas') as Canvas
					if (!silentSameContextRefresh) {
						canvas.zoom('fit-viewport')
					}
					setXmlLoaded(true)
					setLoading(false)
				})
				.catch((err) => {
					if (cancelled) return
					console.error('Error importing BPMN XML:', err)
					setError(err instanceof Error ? err.message : t('components.bpmnEditor.errorInitializing'))
					setXmlLoaded(false)
					setLoading(false)
				})

		void modeler
			.saveXML({ format: true })
			.then(({ xml }) => {
				if (cancelled) return
				const normalizedEditor = normalizeBpmnXmlForCompare(xml || '')
				if (normalizedIncoming === normalizedEditor) {
					lastImportedNormalizedRef.current = normalizedIncoming
					lastSuccessfulImportContextRef.current = layoutCtx
					setXmlLoaded(true)
					setLoading(false)
					return
				}
				if (
					shouldSkipStaleBpmnReimport({
						normalizedIncoming,
						normalizedEditorXml: normalizedEditor,
						isSaveInFlight,
						saveStatus,
						hasUnsavedChanges: unsavedChangesRef.current,
					})
				) {
					setXmlLoaded(true)
					setLoading(false)
					return
				}
				void runImport()
			})
			.catch((err) => {
				if (cancelled) return
				const message = err instanceof Error ? err.message : String(err)
				if (!/no definitions loaded/i.test(message)) {
					console.error('Error serializing BPMN before import:', err)
				}
				void runImport()
			})

		return () => {
			cancelled = true
		}
	}, [autosaveContextKey, initialXml, modelerReady, t, isSaveInFlight, saveStatus])

	// Handle fullscreen change events
	useEffect(() => {
		const handleFullscreenChange = () => {
			const doc = document as Document & {
				webkitFullscreenElement?: Element | null
				mozFullScreenElement?: Element | null
				msFullscreenElement?: Element | null
			}
			const isCurrentlyFullscreen = !!(
				document.fullscreenElement ||
				doc.webkitFullscreenElement ||
				doc.mozFullScreenElement ||
				doc.msFullscreenElement
			)
			setIsFullscreen(isCurrentlyFullscreen)
		}

		document.addEventListener('fullscreenchange', handleFullscreenChange)
		document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
		document.addEventListener('mozfullscreenchange', handleFullscreenChange)
		document.addEventListener('MSFullscreenChange', handleFullscreenChange)

		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange)
			document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
			document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
			document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
		}
	}, [])

	const handleUndo = useCallback(() => {
		const m = modelerRef.current
		if (!m) return
		try {
			const stack = m.get('commandStack') as CommandStackLike
			if (!stack.canUndo()) return
			stack.undo()
		} catch {
			// NavigatedViewer has no command stack
		}
	}, [])

	const handleRedo = useCallback(() => {
		const m = modelerRef.current
		if (!m) return
		try {
			const stack = m.get('commandStack') as CommandStackLike
			if (!stack.canRedo()) return
			stack.redo()
		} catch {
			// NavigatedViewer has no command stack
		}
	}, [])

	const handleOpenFind = useCallback(() => {
		const m = modelerRef.current
		if (!m) return

		try {
			const searchPad = m.get('searchPad') as SearchPadLike | undefined
			if (!searchPad || typeof searchPad.open !== 'function') {
				return
			}
			// Defer to the next task so the current click event finishes bubbling
			// to document before diagram-js registers its "click outside → close" listener.
			// Without this deferral the listener catches the same button click and
			// immediately closes the pad that was just opened.
			setTimeout(() => searchPad.open(), 0)
		} catch (err) {
			console.warn('Could not open BPMN search pad:', err)
		}
	}, [])

	const handleZoomIn = useCallback(() => {
		if (modelerRef.current) {
			const canvas = modelerRef.current.get('canvas') as Canvas
			const currentZoom = canvas.zoom()
			canvas.zoom(currentZoom + 0.1)
		}
	}, [])

	const handleZoomOut = useCallback(() => {
		if (modelerRef.current) {
			const canvas = modelerRef.current.get('canvas') as Canvas
			const currentZoom = canvas.zoom()
			canvas.zoom(Math.max(0.1, currentZoom - 0.1))
		}
	}, [])

	const handleFitViewport = useCallback(() => {
		if (modelerRef.current) {
			const canvas = modelerRef.current.get('canvas') as Canvas
			canvas.zoom('fit-viewport')
		}
	}, [])

	const resolveFullscreenElement = useCallback((): HTMLElement | null => {
		if (toolbarPlacement === 'external' && fullscreenTargetRef?.current) {
			return fullscreenTargetRef.current
		}
		return editorWrapperRef.current
	}, [toolbarPlacement, fullscreenTargetRef])

	const handleFullscreen = useCallback(async () => {
		const fullscreenElement = resolveFullscreenElement()
		if (!fullscreenElement) return

		try {
			if (!isFullscreen) {
				// Enter fullscreen
				const element = fullscreenElement as HTMLElement & {
					webkitRequestFullscreen?: () => Promise<void>
					mozRequestFullScreen?: () => Promise<void>
					msRequestFullscreen?: () => Promise<void>
				}
				if (element.requestFullscreen) {
					await element.requestFullscreen()
				} else if (element.webkitRequestFullscreen) {
					await element.webkitRequestFullscreen()
				} else if (element.mozRequestFullScreen) {
					await element.mozRequestFullScreen()
				} else if (element.msRequestFullscreen) {
					await element.msRequestFullscreen()
				}
			} else {
				// Exit fullscreen
				const doc = document as Document & {
					webkitExitFullscreen?: () => Promise<void>
					mozCancelFullScreen?: () => Promise<void>
					msExitFullscreen?: () => Promise<void>
				}
				if (document.exitFullscreen) {
					await document.exitFullscreen()
				} else if (doc.webkitExitFullscreen) {
					await doc.webkitExitFullscreen()
				} else if (doc.mozCancelFullScreen) {
					await doc.mozCancelFullScreen()
				} else if (doc.msExitFullscreen) {
					await doc.msExitFullscreen()
				}
			}
		} catch (err) {
			console.error('Error toggling fullscreen:', err)
		}
	}, [isFullscreen, resolveFullscreenElement])

	const handleRetrySave = useCallback(async () => {
		if (!modelerRef.current || !onSave) return

		try {
			const { xml } = await modelerRef.current.saveXML({ format: true })
			if (xml) {
				lastImportedNormalizedRef.current = normalizeBpmnXmlForCompare(xml)
				await performManualSave(xml)
			}
		} catch (err) {
			console.error('Error saving BPMN:', err)
		}
	}, [onSave, performManualSave])

	useEffect(() => {
		if (!registerManualSave) return

		registerManualSave(async () => {
			if (!modelerRef.current || !onSaveRef.current) return false

			try {
				const { xml } = await modelerRef.current.saveXML({ format: true })
				if (!xml) return false

				await performManualSaveRef.current(xml)
				return true
			} catch (err) {
				console.warn('Save before leave failed:', err)
				return false
			}
		})

		return () => {
			registerManualSave(null)
		}
	}, [registerManualSave])

	const downloadBlob = useCallback((blob: Blob, filename: string) => {
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = filename
		a.click()
		URL.revokeObjectURL(url)
	}, [])

	const handleExportBpmn = useCallback(async () => {
		if (!modelerRef.current || !containerRef.current) return
		try {
			setExportingFormat('bpmn')
			const { xml } = await modelerRef.current.saveXML({ format: true })
			if (xml) {
				const blob = new Blob([xml], { type: 'application/xml' })
				downloadBlob(blob, 'diagram.bpmn')
			}
		} catch (err) {
			captureUnexpectedError(err)
			setError(t('components.bpmnEditor.errorExportingBpmn'))
		} finally {
			setExportingFormat(null)
		}
	}, [downloadBlob, t])

	const handleExportPng = useCallback(async () => {
		if (!modelerRef.current) return
		try {
			setExportingFormat('png')
			const { svg } = await modelerRef.current.saveSVG()
			if (!svg) return
			const pixelRatio = 2
			const svgBlob = new Blob([svg], { type: 'image/svg+xml' })
			const url = URL.createObjectURL(svgBlob)
			const img = new Image()
			await new Promise<void>((resolve, reject) => {
				img.onload = () => resolve()
				img.onerror = () => reject(new Error('Failed to load SVG for PNG export'))
				img.src = url
			})
			const w = img.naturalWidth * pixelRatio
			const h = img.naturalHeight * pixelRatio
			const canvas = document.createElement('canvas')
			canvas.width = w
			canvas.height = h
			const ctx = canvas.getContext('2d')
			if (!ctx) throw new Error('Could not get canvas context')
			ctx.scale(pixelRatio, pixelRatio)
			ctx.drawImage(img, 0, 0)
			URL.revokeObjectURL(url)
			const blob = await new Promise<Blob | null>((resolve) => {
				canvas.toBlob((b) => resolve(b), 'image/png', 1)
			})
			if (blob) downloadBlob(blob, 'diagram.png')
		} catch (err) {
			captureUnexpectedError(err)
			setError(t('components.bpmnEditor.errorExportingPng'))
		} finally {
			setExportingFormat(null)
		}
	}, [downloadBlob, t])

	const handleExportSvg = useCallback(async () => {
		if (!modelerRef.current) return
		try {
			setExportingFormat('svg')
			const { svg } = await modelerRef.current.saveSVG()
			if (svg) {
				const blob = new Blob([svg], { type: 'image/svg+xml' })
				downloadBlob(blob, 'diagram.svg')
			}
		} catch (err) {
			captureUnexpectedError(err)
			setError(t('components.bpmnEditor.errorExportingSvg'))
		} finally {
			setExportingFormat(null)
		}
	}, [downloadBlob, t])

	const handleExportVisio = useCallback(async () => {
		if (!projectId || !analysisId) return
		try {
			setExportingFormat('visio')
			const { data, error } = await exportModelToVisio({
				path: { project_id: projectId, analysis_id: analysisId },
				parseAs: 'blob',
			})
			if (error || !data) {
				captureUnexpectedError(new Error('exportModelToVisio returned no blob'), {
					extra: { responseError: error },
				})
				setError(t('components.bpmnEditor.errorExportingVisio'))
				return
			}
			const blob = data as Blob
			const downloadUrl = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = downloadUrl
			a.download = 'diagram.vsdx'
			a.click()
			URL.revokeObjectURL(downloadUrl)
		} catch (err) {
			captureUnexpectedError(err)
			setError(t('components.bpmnEditor.errorExportingVisio'))
		} finally {
			setExportingFormat(null)
		}
	}, [projectId, analysisId, t])

	const handleExportPptx = useCallback(async () => {
		if (!projectId || !analysisId) return
		try {
			setExportingFormat('pptx')
			const { data, error } = await exportModelToPptx({
				path: { project_id: projectId, analysis_id: analysisId },
				parseAs: 'blob',
			})
			if (error || !data) {
				captureUnexpectedError(new Error('exportModelToPptx returned no blob'), {
					extra: { responseError: error },
				})
				setError(t('components.bpmnEditor.errorExportingPowerPoint'))
				return
			}
			const blob = data as Blob
			const downloadUrl = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = downloadUrl
			a.download = 'diagram.pptx'
			a.click()
			URL.revokeObjectURL(downloadUrl)
		} catch (err) {
			captureUnexpectedError(err)
			setError(t('components.bpmnEditor.errorExportingPowerPoint'))
		} finally {
			setExportingFormat(null)
		}
	}, [projectId, analysisId, t])

	const onExternalToolbarApiRef = useRef(onExternalToolbarApi)
	onExternalToolbarApiRef.current = onExternalToolbarApi

	useEffect(() => {
		if (toolbarPlacement !== 'external') return

		const notify = onExternalToolbarApiRef.current
		if (!notify) return

		if (!initialXml || error) {
			notify(null)
			return () => notify(null)
		}

		notify({
			loading,
			canUndo,
			canRedo,
			isFullscreen,
			onUndo: handleUndo,
			onRedo: handleRedo,
			onOpenFind: handleOpenFind,
			onZoomIn: handleZoomIn,
			onZoomOut: handleZoomOut,
			onFitViewport: handleFitViewport,
			onToggleFullscreen: () => void handleFullscreen(),
			saveStatus,
			isSaveInFlight,
			isSaveStalled,
			onRetrySave: () => void handleRetrySave(),
			hasAutosave: Boolean(onSave),
			exportingFormat,
			onExportPng: handleExportPng,
			onExportSvg: handleExportSvg,
			onExportBpmn: handleExportBpmn,
			onExportVisio: handleExportVisio,
			onExportPptx: handleExportPptx,
			projectId,
			analysisId,
		})

		return () => notify(null)
	}, [
		toolbarPlacement,
		initialXml,
		error,
		loading,
		canUndo,
		canRedo,
		isFullscreen,
		handleUndo,
		handleRedo,
		handleOpenFind,
		handleZoomIn,
		handleZoomOut,
		handleFitViewport,
		handleFullscreen,
		handleRetrySave,
		handleExportPng,
		handleExportSvg,
		handleExportBpmn,
		handleExportVisio,
		handleExportPptx,
		saveStatus,
		isSaveInFlight,
		isSaveStalled,
		onSave,
		exportingFormat,
		projectId,
		analysisId,
	])

	if (!initialXml) {
		return (
			<div className={`flex items-center justify-center h-96 bg-gray-50 rounded-lg ${className}`}>
				<p className="text-gray-500">{t('components.bpmnEditor.noData')}</p>
			</div>
		)
	}

	if (error) {
		return (
			<div
				className={`flex flex-col items-center justify-center h-96 bg-red-50 rounded-lg ${className}`}
			>
				<p className="text-red-600 mb-2">{t('components.bpmnEditor.errorInitializing')}</p>
				<p className="text-sm text-red-500">{error}</p>
			</div>
		)
	}

	const isExternalToolbar = toolbarPlacement === 'external'

	return (
		<div ref={editorWrapperRef} className={`relative flex h-full min-h-0 flex-col ${className}`}>
			{!isExternalToolbar ? (
				<div
					className="flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white p-2"
					data-tour={TOUR.bpmnToolbar}
				>
					<div className="flex items-center gap-2">
						{toolbarLeft}
						<BpmnDiagramControls
							loading={loading}
							canUndo={readOnly ? false : canUndo}
							canRedo={readOnly ? false : canRedo}
							isFullscreen={isFullscreen}
							onUndo={handleUndo}
							onRedo={handleRedo}
							onOpenFind={handleOpenFind}
							onZoomIn={handleZoomIn}
							onZoomOut={handleZoomOut}
							onFitViewport={handleFitViewport}
							onToggleFullscreen={() => void handleFullscreen()}
							showTourTargets={showTourTargets}
						/>
						<BpmnExportMenu
							loading={loading}
							exportingFormat={exportingFormat}
							onExportPng={handleExportPng}
							onExportSvg={handleExportSvg}
							onExportBpmn={handleExportBpmn}
							onExportVisio={handleExportVisio}
							onExportPptx={handleExportPptx}
							projectId={projectId}
							analysisId={analysisId}
							showTourTargets={showTourTargets}
						/>
					</div>
					<div className="flex items-center gap-2">
						{!readOnly ? (
							<BpmnModelSaveControls
								loading={loading}
								saveStatus={saveStatus}
								isSaveInFlight={isSaveInFlight}
								isSaveStalled={isSaveStalled}
								onRetrySave={() => void handleRetrySave()}
								hasAutosave={Boolean(onSave)}
							/>
						) : null}
						{toolbarExtra}
					</div>
				</div>
			) : null}

			{previewBanner}

			{/* Editor container */}
			<div
				ref={setCanvasBoundary}
				className={`relative min-h-0 flex-1 overflow-hidden bg-gray-50 ${isExternalToolbar ? '' : 'rounded-b-lg'}`}
				data-tour={TOUR.bpmnCanvas}
			>
				<div ref={containerRef} className="w-full h-full relative" />
				<ImprovementCanvasLayer
					modeler={activeModeler}
					xmlLoaded={xmlLoaded}
					boundary={canvasBoundary}
				/>
			</div>

			{/* Cover full editor (toolbar + canvas) while loading to avoid visual flash */}
			{loading && (
				<div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white">
					<Spinner className="h-8 w-8" />
				</div>
			)}
		</div>
	)
}

export const BpmnEditor = memo(BpmnEditorInner)
